/* eslint-disable react-refresh/only-export-components */
/**
 * Logger with Context Hook
 *
 * Provides logging functionality using Grafana Faro with automatic context inclusion.
 * Includes persistent context (user, entity, path) in every log call.
 */

import { createContext, useContext, useMemo, useCallback, useRef, ReactNode } from 'react';
import { useLogger } from '@opengov/psc-react-logger';
import type { Logger, LogFunction, LogOptions } from '../../types';

/**
 * Context value for LoggerContextProvider
 */
interface LoggerContextValue {
  /**
   * Update the persistent context that will be included in all logs
   */
  updatePersistentContext: (context: Record<string, unknown>) => void;
  /**
   * Get the current persistent context
   */
  getPersistentContext: () => Record<string, unknown>;
}

const LoggerContext = createContext<LoggerContextValue | undefined>(undefined);

interface LoggerContextProviderProps {
  children: ReactNode;
}

/**
 * Provider that manages persistent logger context using useRef.
 * Context is updated throughout the app runtime to reflect current user, entity, and path.
 */
export function LoggerContextProvider({ children }: LoggerContextProviderProps) {
  const contextRef = useRef<Record<string, unknown>>({});

  const updatePersistentContext = useCallback((context: Record<string, unknown>): void => {
    contextRef.current = { ...contextRef.current, ...context };
  }, []);

  const getPersistentContext = useCallback((): Record<string, unknown> => {
    return { ...contextRef.current };
  }, []);

  // Memoized so consumers keep a stable logger: `useLoggerWithContext` rebuilds its logger
  // whenever this value changes, and hooks that list the logger as a dependency re-run with it.
  const value = useMemo<LoggerContextValue>(
    () => ({ updatePersistentContext, getPersistentContext }),
    [updatePersistentContext, getPersistentContext]
  );

  return <LoggerContext.Provider value={value}>{children}</LoggerContext.Provider>;
}

/**
 * Hook that provides a logger with automatic context inclusion.
 * Automatically adds persistent context (user, entity, path) to every log call.
 *
 * @returns Logger interface with wrapped methods that include context
 */
export function useLoggerWithContext(): Logger {
  const baseLogger = useLogger();
  const loggerContext = useContext(LoggerContext);

  const createLogFunction = useCallback(
    (baseLogFn: LogFunction): LogFunction => {
      return (
        message: string,
        additionalContext?: Record<string, unknown>,
        options?: LogOptions
      ) => {
        // If persistContext is true, update the persistent context with the provided context
        if (options?.persistContext && additionalContext && loggerContext) {
          loggerContext.updatePersistentContext(additionalContext);
        }

        // Get current persistent context and merge with additional context
        const persistentContext = loggerContext?.getPersistentContext() ?? {};
        const mergedContext = {
          ...persistentContext,
          ...additionalContext,
        };
        // Call base logger without options (base logger doesn't support options)
        baseLogFn(message, mergedContext);
      };
    },
    [loggerContext]
  );

  const logger = useMemo<Logger>(
    () => ({
      log: createLogFunction(baseLogger.log),
      info: createLogFunction(baseLogger.info),
      warn: createLogFunction(baseLogger.warn),
      error: createLogFunction(baseLogger.error),
    }),
    [baseLogger, createLogFunction]
  );

  return logger;
}

/**
 * Hook to access logger context provider functions.
 * Use this to update persistent context when user, entity, or path changes.
 */
export function useLoggerContext(): LoggerContextValue {
  const context = useContext(LoggerContext);
  if (context === undefined) {
    throw new Error('useLoggerContext must be used within a LoggerContextProvider');
  }
  return context;
}
